"use client";

import {cn} from "@/lib/utils";
import {useEffect, useRef} from "react";
import Image from "next/image";
import {gsap, ScrollTrigger} from '@/lib/gsap';

interface ComponentProps {
    className?: string
}

export default function ParallaxArrow({className}: ComponentProps) {
    const arrowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!arrowRef.current || typeof window === 'undefined') return;


        const arrowBottom = arrowRef.current.querySelector('.arrow-bottom');
        const arrowMiddle = arrowRef.current.querySelector('.arrow-middle');
        const arrowTop = arrowRef.current.querySelector('.arrow-top');

        // Initial animation to bring in the arrows
        gsap.fromTo(
            [arrowBottom, arrowMiddle, arrowTop],
            {
                x: gsap.utils.wrap([-100, -70, -40]),
                opacity: 0
            },
            {
                x: 0,
                opacity: 1,
                duration: 1.5,
                stagger: 0.2,
                delay: 0.5,
                ease: "power2.out"
            }
        );

        // Create parallax scrolling effects for each layer
        gsap.to(arrowBottom, {
            scrollTrigger: {
                trigger: arrowRef.current,
                start: "top bottom",
                end: "bottom top",
                scrub: 1.5,
            },
            x: 50,
            ease: "none"
        });

        gsap.to(arrowMiddle, {
            scrollTrigger: {
                trigger: arrowRef.current,
                start: "top bottom",
                end: "bottom top",
                scrub: 1,
            },
            x: 25,
            ease: "none"
        });

        gsap.to(arrowTop, {
            scrollTrigger: {
                trigger: arrowRef.current,
                start: "top bottom",
                end: "bottom top",
                scrub: 0.5,
            },
            x: 10,
            ease: "none"
        });

        return () => {
            ScrollTrigger.getAll().forEach(t => t.kill());
        };
    }, []);

    return (
        <div ref={arrowRef}
             className={cn("", className)}>
            <div className="container mx-auto relative px-6 md:px-0">
                {/* TODO: playaround with viewport specific max-heights for hte parallax arrow
                NOTE: `max-h-36` is temporary */}
                <div className="flex flex-col items-center justify-center max-h-36">
                   {/* <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold text-white">Discover Our Comedy Events</h2>
                        <p className="text-white/80 mt-4">Follow the arrow to see what we offer</p>
                    </div>*/}

                    <div className="relative w-full max-w-lg h-64 md:h-80">
                        {/* Layer 1: Bottom arrow (moves the most) */}
                        <div className="arrow-bottom absolute inset-0">
                            <Image
                                src="/images/atc-arrow/atc-arrow-parallax/0-arrow-bottom.png"
                                alt="Red arrow bottom layer"
                                fill
                                sizes="(max-width: 768px) 100vw, 500px"
                                style={{objectFit: 'contain'}}
                            />
                        </div>

                        {/* Layer 2: Middle arrow (moves moderately) */}
                        <div className="arrow-middle absolute inset-0">
                            <Image
                                src="/images/atc-arrow/atc-arrow-parallax/1-arrow-middle.png"
                                alt="Red arrow middle layer"
                                fill
                                sizes="(max-width: 768px) 100vw, 500px"
                                style={{objectFit: 'contain'}}
                            />
                        </div>

                        {/* Layer 3: Top arrow (moves the least) */}
                        <div className="arrow-top absolute inset-0">
                            <Image
                                src="/images/atc-arrow/atc-arrow-parallax/2-arrow-top.png"
                                alt="Red arrow with text"
                                fill
                                sizes="(max-width: 768px) 100vw, 500px"
                                style={{objectFit: 'contain'}}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
